import React, { useState, DragEvent } from 'react';
import { useReadOnlyUi } from '../../../../../store/featureStore';
import { Kabelverteilschrank, KvsBusbar, Abgangsleitung, ConnectedLine } from '../../../../../types/network';
import { LineSegmentState } from '../../../../../store/types/network';
import { useNetworkElementsStore } from '../../../../../store/networkElementsStore';
import kvsService from '../../../../../services/kvsService';
import { ChevronIcon } from '../../../../icons/ButtonIcons';
import { CableRow } from './CableRow';
import { CouplingRow } from '../../shared/CouplingRow';
import { useCouplingStore } from '../../../../../store/couplingStore';
import { useZoneStore } from '../../../../../store/zoneStore';
import { useCouplingSwitch } from '../../../../shared/CouplingSwitchDialog';
import type { ZoneCoupling } from '../../../../../types/coupling';

interface BusbarsSectionProps {
  kvs: Kabelverteilschrank;
  allCables: Abgangsleitung[];
  segments: Record<string, LineSegmentState>;
  thresholds: { green: number; yellow: number; orange: number; red: number };
  onUpdate: () => void;
}

export const BusbarsSection: React.FC<BusbarsSectionProps> = ({
  kvs,
  allCables,
  segments,
  thresholds,
  onUpdate
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  // Read-only deployments: busbars/switches are displayed, never edited.
  const readOnly = useReadOnlyUi();
  const [isAddingBusbar, setIsAddingBusbar] = useState(false);
  const [newBusbarName, setNewBusbarName] = useState('');
  const [isAddingSwitch, setIsAddingSwitch] = useState(false);
  const [switchBusbarA, setSwitchBusbarA] = useState('');
  const [switchBusbarB, setSwitchBusbarB] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [editingBusbar, setEditingBusbar] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [dragOverBusbarId, setDragOverBusbarId] = useState<string | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const loadElements = useNetworkElementsStore(state => state.loadElements);

  const busbars = kvs.busbars || [];
  const busbarSwitches = kvs.busbarSwitches || [];

  // Trennstellen whose fuse sits in this KVS. Their coupling cable belongs to the neighbour
  // zone (NETZVERBUND.md 4.2), so it is not among allCables - without these rows the busbar
  // view hid the very Abgang that couples the two zones. switchBusbarId null = busbar 0.
  const activeZoneId = useZoneStore(state => state.activeZoneId);
  const allCouplings = useCouplingStore(state => state.couplings);
  const setCouplingBusbar = useCouplingStore(state => state.setBusbar);
  const { requestSwitch, dialog: couplingDialog } = useCouplingSwitch();
  const kvsCouplings = React.useMemo(() => allCouplings.filter(c => c.switchElementId === kvs.id), [allCouplings, kvs.id]);
  const defaultBusbarId = React.useMemo(
    () => [...(kvs.busbars ?? [])].sort((a, b) => (a.busbarIndex ?? 0) - (b.busbarIndex ?? 0))[0]?.id ?? null,
    [kvs.busbars]
  );
  const getCouplingsForBusbar = (busbarId: string): ZoneCoupling[] =>
    kvsCouplings.filter(c => (c.switchBusbarId ?? defaultBusbarId) === busbarId);

  const handleAddBusbar = async () => {
    if (!newBusbarName.trim()) {
      setError('Bitte geben Sie einen Namen ein');
      return;
    }
    setError(null);
    const result = await kvsService.addBusbar(kvs.id, { name: newBusbarName.trim() });
    if (result.success) {
      setNewBusbarName('');
      setIsAddingBusbar(false);
      await loadElements();
      onUpdate();
    } else {
      setError(result.error || 'Fehler beim Hinzufügen der Sammelschiene');
    }
  };

  const handleUpdateBusbar = async (busbarId: string) => {
    if (!editName.trim()) return;
    setError(null);
    const result = await kvsService.updateBusbar(kvs.id, busbarId, { name: editName.trim() });
    if (result.success) {
      setEditingBusbar(null);
      setEditName('');
      await loadElements();
      onUpdate();
    } else {
      setError(result.error || 'Fehler beim Aktualisieren');
    }
  };

  const handleDeleteBusbar = async (busbarId: string) => {
    setError(null);
    const result = await kvsService.deleteBusbar(kvs.id, busbarId);
    if (result.success) {
      await loadElements();
      onUpdate();
    } else {
      setError(result.error || 'Fehler beim Löschen');
    }
  };

  const handleAddSwitch = async () => {
    if (!switchBusbarA || !switchBusbarB) {
      setError('Bitte wählen Sie zwei Sammelschienen');
      return;
    }
    if (switchBusbarA === switchBusbarB) {
      setError('Bitte wählen Sie zwei verschiedene Sammelschienen');
      return;
    }
    setError(null);
    const result = await kvsService.addBusbarSwitch(kvs.id, {
      busbarAId: switchBusbarA,
      busbarBId: switchBusbarB
    });
    if (result.success) {
      setSwitchBusbarA('');
      setSwitchBusbarB('');
      setIsAddingSwitch(false);
      await loadElements();
      onUpdate();
    } else {
      setError(result.error || 'Fehler beim Hinzufügen der Kupplung');
    }
  };

  const handleToggleSwitch = async (switchId: string, currentState: boolean) => {
    setError(null);
    const result = await kvsService.toggleBusbarSwitch(kvs.id, switchId, !currentState);
    if (result.success) {
      await loadElements();
      onUpdate();
    } else {
      setError(result.error || 'Fehler beim Umschalten');
    }
  };

  const handleDeleteSwitch = async (switchId: string) => {
    setError(null);
    const result = await kvsService.deleteBusbarSwitch(kvs.id, switchId);
    if (result.success) {
      await loadElements();
      onUpdate();
    } else {
      setError(result.error || 'Fehler beim Löschen');
    }
  };

  const getBusbarName = (busbarId: string) => {
    const busbar = busbars.find(b => b.id === busbarId);
    return busbar?.name || 'Unbekannt';
  };

  const getCablesForBusbar = (busbar: KvsBusbar): { cable: Abgangsleitung; connectedLine: ConnectedLine }[] => {
    if (!busbar.connectedLines || busbar.connectedLines.length === 0) return [];
    return busbar.connectedLines
      .map(cl => {
        const cable = allCables.find(c => c.id === cl.id);
        return cable ? { cable, connectedLine: cl } : null;
      })
      .filter((item): item is { cable: Abgangsleitung; connectedLine: ConnectedLine } => item !== null);
  };

  // Get all cables connected to this KVS (from kvs.connectedLines)
  const getAllConnectedCables = (): Abgangsleitung[] => {
    if (!kvs.connectedLines || kvs.connectedLines.length === 0) return [];
    return kvs.connectedLines
      .map(cl => allCables.find(cable => cable.id === cl.id))
      .filter((cable): cable is Abgangsleitung => cable !== undefined);
  };

  // Get cables that are connected to KVS but not assigned to any specific busbar
  const getUnassignedCables = (): Abgangsleitung[] => {
    const allConnected = getAllConnectedCables();
    const assignedCableIds = new Set<string>();

    for (const busbar of busbars) {
      if (busbar.connectedLines) {
        for (const cl of busbar.connectedLines) {
          assignedCableIds.add(cl.id);
        }
      }
    }

    return allConnected.filter(cable => !assignedCableIds.has(cable.id));
  };

  // Drag and drop handlers
  const handleDragStart = (e: DragEvent<HTMLDivElement>, cableId: string) => {
    e.dataTransfer.setData('cableId', cableId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCouplingDragStart = (e: DragEvent<HTMLDivElement>, couplingId: string) => {
    e.dataTransfer.setData('couplingId', couplingId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>, busbarId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverBusbarId(busbarId);
  };

  const handleDragLeave = () => {
    setDragOverBusbarId(null);
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>, targetBusbarId: string) => {
    e.preventDefault();
    setDragOverBusbarId(null);

    const couplingId = e.dataTransfer.getData('couplingId');
    if (couplingId) {
      setError(null);
      setIsAssigning(true);
      try {
        // busbar 0 is stored as null so the default stays the default
        await setCouplingBusbar(couplingId, targetBusbarId === defaultBusbarId ? null : targetBusbarId);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Fehler beim Zuordnen der Trennstelle');
      } finally {
        setIsAssigning(false);
      }
      return;
    }

    const cableId = e.dataTransfer.getData('cableId');
    if (!cableId) {
      return;
    }

    setError(null);
    setIsAssigning(true);

    try {
      const result = await kvsService.assignCableToBusbar(kvs.id, targetBusbarId, cableId);
      if (result.success) {
        await loadElements();
        onUpdate();
      } else {
        console.error('[BusbarsSection] Assignment failed:', result.error);
        setError(result.error || 'Fehler beim Zuordnen des Kabels');
      }
    } catch (err) {
      console.error('[BusbarsSection] Exception during assignment:', err);
      setError('Fehler beim Zuordnen des Kabels');
      console.error('[BusbarsSection] Error assigning cable:', err);
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className="relative bg-white dark:bg-primary-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-primary-600">
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-slate-400 dark:bg-slate-500" />
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left pl-4 pr-3 py-2.5 hover:bg-gray-50 dark:hover:bg-primary-700/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="shrink-0 w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-900/40 flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-slate-600 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </div>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Sammelschienen & Kabel</span>
          <span className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">
            {(() => {
              const totalCables = busbars.reduce((sum, b) => sum + (b.connectedLines?.length || 0), 0) + getUnassignedCables().length;
              return `${totalCables} Kabel${kvsCouplings.length > 0 ? ` · ${kvsCouplings.length} Trennstellen` : ''}`;
            })()}
          </span>
        </div>
        <ChevronIcon
          direction={isExpanded ? "up" : "down"}
          className="w-4 h-4 text-gray-400 dark:text-gray-500"
        />
      </button>

      {isExpanded && (
        <div className="pl-4 pr-3 pb-3">
          {error && (
            <div className="mb-2 px-2.5 py-1.5 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded-md">
              {error}
            </div>
          )}

          {/* Unassigned Cables Section */}
          {(() => {
            const unassignedCables = getUnassignedCables();
            if (unassignedCables.length === 0) return null;

            return (
              <div className="mb-2 px-2.5 py-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-200 dark:border-yellow-800">
                <div className="text-xs font-medium text-yellow-700 dark:text-yellow-400 mb-1">
                  Nicht zugeordnet ({unassignedCables.length})
                </div>
                <div className="text-[10px] text-yellow-600 dark:text-yellow-500 mb-1.5">
                  Per Drag & Drop in eine Sammelschiene ziehen
                </div>
                <div className="space-y-0.5">
                  {unassignedCables.map(cable => {
                    const cl = kvs.connectedLines?.find(c => c.id === cable.id);
                    if (!cl) return null;
                    return (
                      <CableRow
                        key={cable.id}
                        cable={cable}
                        connectedLine={cl}
                        kvsId={kvs.id}
                        busIndex={kvs.busIndex}
                        segments={segments}
                        thresholds={thresholds}
                        onDragStart={handleDragStart}
                        onUpdate={onUpdate}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Loading indicator for assignment */}
          {isAssigning && (
            <div className="mb-2 px-2.5 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-xs text-blue-600 dark:text-blue-400 text-center rounded-md">
              Kabel wird zugeordnet...
            </div>
          )}

          {/* List of Busbars */}
          <div className="space-y-1.5">
            {busbars.map((busbar) => {
              const cables = getCablesForBusbar(busbar);
              const busbarCouplings = activeZoneId ? getCouplingsForBusbar(busbar.id) : [];
              const isEditing = editingBusbar === busbar.id;
              const isDragOver = dragOverBusbarId === busbar.id;

              return (
                <div
                  key={busbar.id}
                  className={`bg-gray-50 dark:bg-primary-700/50 rounded-md border p-2.5 transition-colors ${
                    isDragOver
                      ? 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-100 dark:border-primary-600'
                  }`}
                  onDragOver={(e) => handleDragOver(e, busbar.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, busbar.id)}
                >

                  <div className="flex items-center justify-between mb-1">
                    {isEditing ? (
                      <div className="flex items-center gap-1.5 flex-1">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="flex-1 text-xs border border-gray-300 dark:border-primary-500 bg-white dark:bg-primary-600 text-gray-900 dark:text-white rounded px-2 py-1"
                          autoFocus
                        />
                        <button
                          onClick={() => handleUpdateBusbar(busbar.id)}
                          className="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                        >
                          OK
                        </button>
                        <button
                          onClick={() => { setEditingBusbar(null); setEditName(''); }}
                          className="text-xs bg-gray-200 dark:bg-primary-500 text-gray-700 dark:text-white px-2 py-1 rounded hover:bg-gray-300 dark:hover:bg-primary-400"
                        >
                          X
                        </button>
                      </div>
                    ) : (
                      <>
                        <div>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            {busbar.name}
                          </span>
                          {!busbar.inService && (
                            <span className="ml-1.5 text-[10px] bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded">
                              außer Betrieb
                            </span>
                          )}
                          <div className="text-[10px] text-gray-500 dark:text-gray-400">
                            {cables.length} Kabel{busbarCouplings.length > 0 ? ` · ${busbarCouplings.length} Trennstellen` : ''} &middot; Bus #{busbar.busIndex ?? '-'}
                          </div>
                        </div>
                        {!readOnly && (
                        <div className="flex items-center gap-0.5">
                          <button
                            onClick={() => { setEditingBusbar(busbar.id); setEditName(busbar.name); }}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-primary-500 transition-colors"
                            title="Bearbeiten"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          {busbars.length > 1 && cables.length === 0 && busbarCouplings.length === 0 && (
                            <button
                              onClick={() => handleDeleteBusbar(busbar.id)}
                              className="p-1 text-red-400 hover:text-red-600 rounded hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                              title="Löschen"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Connected cables for this busbar */}
                  {cables.length > 0 ? (
                    <div className="space-y-0.5 mt-1.5">
                      {cables.map(({ cable, connectedLine }) => (
                        <CableRow
                          key={cable.id}
                          cable={cable}
                          connectedLine={connectedLine}
                          kvsId={kvs.id}
                          busIndex={busbar.busIndex}
                          segments={segments}
                          thresholds={thresholds}
                          onDragStart={handleDragStart}
                          onUpdate={onUpdate}
                        />
                      ))}
                    </div>
                  ) : busbarCouplings.length === 0 ? (
                    <div className="text-[10px] text-gray-400 dark:text-gray-500 italic mt-1">
                      {isDragOver ? 'Hier ablegen...' : 'Keine Kabel \u2013 hierher ziehen'}
                    </div>
                  ) : null}

                  {/* Trennstellen fused on this busbar: the Abgänge into the neighbour zones */}
                  {busbarCouplings.length > 0 && activeZoneId && (
                    <div className="mt-1.5 space-y-0.5">
                      <div className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
                        Trennstellen ({busbarCouplings.length})
                      </div>
                      {busbarCouplings.map(c => (
                        <CouplingRow
                          key={c.id}
                          coupling={c}
                          activeZoneId={activeZoneId}
                          readOnly={readOnly}
                          onSwitch={target => requestSwitch(c, target)}
                          onDragStart={handleCouplingDragStart}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {couplingDialog}

          {/* Add Busbar */}
          {readOnly ? null : isAddingBusbar ? (
            <div className="mt-2 flex items-center gap-1.5">
              <input
                type="text"
                value={newBusbarName}
                onChange={(e) => setNewBusbarName(e.target.value)}
                placeholder="Name der Sammelschiene"
                className="flex-1 text-xs border border-gray-300 dark:border-primary-500 bg-white dark:bg-primary-600 text-gray-900 dark:text-white rounded-md px-2 py-1"
                autoFocus
              />
              <button onClick={handleAddBusbar} className="text-xs bg-green-500 text-white px-2 py-1 rounded-md hover:bg-green-600">
                OK
              </button>
              <button
                onClick={() => { setIsAddingBusbar(false); setNewBusbarName(''); setError(null); }}
                className="text-xs bg-gray-200 dark:bg-primary-500 text-gray-700 dark:text-white px-2 py-1 rounded-md hover:bg-gray-300 dark:hover:bg-primary-400"
              >
                X
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsAddingBusbar(true)}
              className="mt-2 w-full text-slate-600 dark:text-slate-400 hover:bg-slate-500/10 dark:hover:bg-slate-500/20 border border-slate-300 dark:border-slate-700 h-7 flex items-center justify-center gap-1 rounded-md text-xs"
            >
              + Sammelschiene
            </button>
          )}

          {/* Busbar Switches Section */}
          {busbars.length >= 2 && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-primary-600">
              <div className="flex items-center gap-2 mb-2">
                <div className="shrink-0 w-5 h-5 rounded-full bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                  <svg className="w-3 h-3 text-violet-600 dark:text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Kupplungsschalter</span>
              </div>

              {busbarSwitches.length > 0 && (
                <div className="space-y-1 mb-2">
                  {busbarSwitches.map(sw => (
                    <div key={sw.id} className="flex items-center justify-between bg-gray-50 dark:bg-primary-700/50 rounded-md px-2.5 py-1.5 border border-gray-100 dark:border-primary-600">
                      <span className="text-xs text-gray-700 dark:text-gray-200">
                        {getBusbarName(sw.busbarAId)} <span className="text-gray-400 dark:text-gray-500">\u2194</span> {getBusbarName(sw.busbarBId)}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleToggleSwitch(sw.id, sw.isClosed)}
                          className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                            sw.isClosed
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}
                        >
                          {sw.isClosed ? 'Geschlossen' : 'Offen'}
                        </button>
                        {!readOnly && (
                        <button
                          onClick={() => handleDeleteSwitch(sw.id)}
                          className="p-0.5 text-red-400 hover:text-red-600 rounded hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                          title="Löschen"
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Switch Form */}
              {readOnly ? null : isAddingSwitch ? (
                <div className="space-y-1.5">
                  <div className="flex gap-1.5">
                    <select
                      value={switchBusbarA}
                      onChange={(e) => setSwitchBusbarA(e.target.value)}
                      className="flex-1 text-xs border border-gray-300 dark:border-primary-500 bg-white dark:bg-primary-600 text-gray-900 dark:text-white rounded-md px-2 py-1"
                    >
                      <option value="">Schiene A</option>
                      {busbars.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                    <select
                      value={switchBusbarB}
                      onChange={(e) => setSwitchBusbarB(e.target.value)}
                      className="flex-1 text-xs border border-gray-300 dark:border-primary-500 bg-white dark:bg-primary-600 text-gray-900 dark:text-white rounded-md px-2 py-1"
                    >
                      <option value="">Schiene B</option>
                      {busbars.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={handleAddSwitch} className="text-xs bg-green-500 text-white px-2 py-1 rounded-md hover:bg-green-600">
                      Hinzufügen
                    </button>
                    <button
                      onClick={() => { setIsAddingSwitch(false); setSwitchBusbarA(''); setSwitchBusbarB(''); setError(null); }}
                      className="text-xs bg-gray-200 dark:bg-primary-500 text-gray-700 dark:text-white px-2 py-1 rounded-md hover:bg-gray-300 dark:hover:bg-primary-400"
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setIsAddingSwitch(true)}
                  className="w-full text-violet-600 dark:text-violet-400 hover:bg-violet-500/10 dark:hover:bg-violet-500/20 border border-violet-300 dark:border-violet-700 h-7 flex items-center justify-center gap-1 rounded-md text-xs"
                >
                  + Kupplung
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
